import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, Lightning, ArrowsClockwise, Eraser } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import useConsensusGroups from '../hooks/useConsensusGroups';
import { GROUP_STYLE, GroupLegend, ConsensusRefLink, QualityBadge } from '../components/ConsensusBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const tagColors = { 'H2': 'text-amber-600', 'H3': 'text-orange-600', 'H4': 'text-red-600', 'H1': 'text-green-700', 'M-only': 'text-blue-600', 'L-only': 'text-gray-400' };

const POS_LABELS = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5', lucky_star_1: 'LS1', lucky_star_2: 'LS2' };

const CONSTRAINT_DEFS = [
  { key: 'hml_pattern', label: 'HML Pattern', window: 'W7', path: '/window7' },
  { key: 'sign_pattern', label: 'Diff Sign', window: 'W3', path: '/window3' },
  { key: 'combo_pattern', label: '7-Combo', window: 'W5', path: '/window3-2' },
  { key: 'sum_range_min', label: 'Sum Range', window: 'W14', path: '/window14' },
  { key: 'range_pattern', label: 'Range Pattern', window: 'W24', path: '/window24' },
  { key: 'shared_prediction', label: 'Shared Numbers', window: 'W17', path: '/window17' },
  { key: 'temp_prefs', label: 'Temp Zone Prefs', window: 'W11×W21', path: '/cross-analysis' },
  { key: 'number_picks', label: 'Number Picks', window: 'W6', path: '/window6' },
];

function getDisplay(s, def) {
  if (!s) return null;
  if (def.key === 'sum_range_min') return s.sum_range_min != null ? `${s.sum_range_min} - ${s.sum_range_max}` : null;
  if (def.key === 'shared_prediction' && s.shared_prediction) return s.shared_prediction;
  if (def.key === 'temp_prefs' && s.temp_prefs) {
    const entries = Object.entries(s.temp_prefs).filter(([, v]) => v);
    if (entries.length === 0) return null;
    return entries.map(([k, v]) => `${POS_LABELS[k] || k}=${v}`).join(', ');
  }
  if (def.key === 'number_picks' && s.number_picks) {
    const entries = Object.entries(s.number_picks).filter(([, v]) => v && v.length > 0);
    if (entries.length === 0) return null;
    return entries.map(([k, v]) => `${POS_LABELS[k] || k}=[${v.join(',')}]`).join('  ');
  }
  const val = s[def.key];
  return typeof val === 'string' && val ? val : null;
}

function ConstraintRow({ def, data, navigate }) {
  const display = getDisplay(data, def);
  return (
    <tr className={`border-b border-gray-100 ${display ? '' : 'opacity-40'}`} data-testid={`row-${def.key}`}>
      <td className="px-3 py-2.5 text-xs font-bold text-[#002FA7]">{def.window}</td>
      <td className="px-3 py-2.5 text-xs font-medium text-gray-700">{def.label}</td>
      <td className="px-3 py-2.5">
        {display ? (
          <span className="font-mono font-bold text-sm text-gray-900">{display}</span>
        ) : (
          <span className="text-xs text-gray-400 italic">Not set</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-center">
        {display ? (
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
        ) : (
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300" />
        )}
      </td>
      <td className="px-3 py-2.5">
        <button onClick={() => navigate(def.path)} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`edit-${def.key}`}>
          {display ? 'Edit' : 'Set'}
        </button>
      </td>
    </tr>
  );
}

function WindowTab({ setKey, data, generating, onGenerate, onClear, results, navigate, consensus }) {
  const applied = CONSTRAINT_DEFS.filter(d => getDisplay(data, d));
  const r = results;

  return (
    <div className="space-y-4" data-testid={`window-${setKey}`}>
      {/* Constraints Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="font-bold text-gray-900">Window {setKey} — Constraints</h2>
            <p className="text-[10px] text-gray-500">{applied.length} of {CONSTRAINT_DEFS.length} constraints set</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${applied.length > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
              {applied.length} / {CONSTRAINT_DEFS.length}
            </span>
            <button onClick={() => onClear(setKey)} disabled={applied.length === 0}
              className="px-3 py-2 bg-white border border-rose-300 text-rose-600 hover:bg-rose-50 rounded-lg font-bold text-xs transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid={`clear-${setKey}`} title="Remove all constraint selections for this set">
              <Eraser size={14} weight="bold" />
              Clear All
            </button>
            <button onClick={() => onGenerate(setKey)} disabled={generating || applied.length === 0}
              className="px-4 py-2 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-lg font-bold text-xs transition disabled:opacity-40 flex items-center gap-2"
              data-testid={`gen-${setKey}`}>
              {generating ? <CircleNotch size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
              Generate {setKey}
            </button>
          </div>
        </div>
        <table className="w-full" data-testid={`table-${setKey}`}>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase w-16">Window</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase w-40">Constraint</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Value</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase w-16">Status</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase w-16">Action</th>
            </tr>
          </thead>
          <tbody>
            {CONSTRAINT_DEFS.map(def => (
              <ConstraintRow key={def.key} def={def} data={data} navigate={navigate} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Generated Tickets */}
      {r?.combinations?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`${setKey}-results`}>
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#002FA7] to-[#001d6a] text-white">
            <h3 className="font-bold text-lg">{setKey} — {r.combinations.length} Tickets{r.range_pattern_count > 1 ? ` (${r.range_pattern_count} patterns × 10)` : ''}</h3>
            <p className="text-xs text-white/60">C1-C22 principles enforced | {r.total_passed_filters?.toLocaleString()} passed</p>
          </div>
          {r.applied_constraints?.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-1.5">
              {r.applied_constraints.map((ac, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-[10px]">
                  <span className="font-bold text-blue-700">{ac.window}</span>
                  <span className="text-gray-400">{ac.label}:</span>
                  <span className="font-mono font-bold text-gray-700">{ac.value}</span>
                </span>
              ))}
            </div>
          )}
          <div className="p-4">
            <TicketGroups data={r} consensus={consensus} />
          </div>
        </div>
      )}
      {r && r.combinations?.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid={`${setKey}-no-results`}>
          <p className="text-sm font-bold text-amber-800 text-center">No tickets for {setKey}</p>
          <p className="text-xs text-amber-600 mt-1 text-center">Constraints too restrictive — relax some</p>
          {r.conflict_diagnostics?.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-[10px] font-bold text-red-700 uppercase">Constraint Conflict Analysis:</p>
              {r.conflict_diagnostics.map((cd, i) => (
                <div key={i} className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 ${
                  cd.type === 'killer' ? 'bg-red-100 border border-red-300' : 'bg-green-50 border border-green-200'
                }`}>
                  <span className={`font-bold shrink-0 ${cd.type === 'killer' ? 'text-red-700' : 'text-green-700'}`}>
                    {cd.type === 'killer' ? 'CONFLICT' : 'OK'}
                  </span>
                  <div>
                    <span className="font-bold text-gray-800">{cd.filter}</span>
                    <span className="text-gray-500 ml-1">({cd.value})</span>
                    {cd.type === 'killer' && (
                      <p className="text-red-600 font-medium mt-0.5">{cd.message}</p>
                    )}
                    {cd.type === 'passed' && (
                      <span className="text-green-600 ml-2">{cd.survived?.toLocaleString()} combos survived</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {r.filter_stats && (
            <div className="mt-3 bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Filter Elimination Chain:</p>
              <div className="flex flex-wrap gap-2 text-[10px]">
                {Object.entries(r.filter_stats).map(([k, v]) => (
                  <span key={k} className={`px-2 py-1 rounded font-mono ${v > 0 ? 'bg-red-100 text-red-700 font-bold' : 'bg-gray-50 text-gray-400'}`}>
                    {k}: {v?.toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TicketGroups({ data, consensus }) {
  const rp = data.range_patterns || [];
  const hasGroups = rp.length > 1;
  const groups = hasGroups
    ? rp.map(r => ({ pattern: r, tickets: data.combinations.filter(t => t.range_group === r) }))
    : [{ pattern: null, tickets: data.combinations }];

  const mixOf = (balls) => {
    const m = { GREEN: 0, YELLOW: 0, RED: 0 };
    let score = 0;
    for (const n of balls || []) { m[consensus.groupOf(n)] += 1; score += consensus.scoreOf(n); }
    let tier;
    if (m.RED >= 2 || score < 0) tier = "CAUTION";
    else if (score >= 5 && m.RED <= 1) tier = "PREMIUM";
    else tier = "SOLID";
    return { mix: m, score, tier };
  };

  return groups.map((group, gIdx) => (
    <div key={`g-${group.pattern || gIdx}`} className="mb-4">
      {hasGroups && (
        <div className="flex items-center gap-2 mb-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
          <span className="text-[10px] font-bold text-indigo-800">Range {gIdx + 1}:</span>
          <span className="font-mono font-bold text-sm text-indigo-700">{group.pattern}</span>
          <span className="text-[10px] text-indigo-500">{group.tickets.length} tickets</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-2 py-2 text-left font-bold text-gray-500">#</th>
              {['B1', 'B2', 'B3', 'B4', 'B5'].map(h => <th key={h} className="px-2 py-2 text-center font-bold text-gray-500">{h}</th>)}
              <th className="px-2 py-2 text-center font-bold text-gray-500">LS1</th>
              <th className="px-2 py-2 text-center font-bold text-gray-500">LS2</th>
              <th className="px-2 py-2 text-center font-bold text-gray-500">HML</th>
              <th className="px-2 py-2 text-center font-bold text-gray-500">D/P</th>
              <th className="px-2 py-2 text-center font-bold text-gray-500 bg-slate-100">G-Y-R</th>
              <th className="px-2 py-2 text-center font-bold text-gray-500 bg-slate-100">Quality</th>
              <th className="px-2 py-2 text-right font-bold text-gray-500">Score</th>
            </tr>
          </thead>
          <tbody>
            {[...group.tickets].map((t, idx) => {
              const balls = (t.ball_profiles || []).map(bp => bp.number).concat(!t.ball_profiles ? (t.balls || []) : []);
              const q = mixOf(balls);
              return { t, balls, q, idx };
            }).sort((a, b) => {
              const ra = a.q.tier === 'PREMIUM' ? 3 : a.q.tier === 'SOLID' ? 2 : 1;
              const rb = b.q.tier === 'PREMIUM' ? 3 : b.q.tier === 'SOLID' ? 2 : 1;
              if (ra !== rb) return rb - ra;
              return a.idx - b.idx;
            }).map(({ t, balls, q, idx: origIdx }, idx) => {
              const mix = q.mix;
              return (
              <tr key={`${gIdx}-${balls.join('-')}-${origIdx}`} className={idx === 0 ? 'bg-emerald-50' : idx % 2 === 0 ? 'bg-gray-50/50' : ''}>
                <td className="px-2 py-2 font-bold text-gray-400">{idx + 1}</td>
                {(t.ball_profiles || []).map((bp, j) => {
                  const g = consensus.groupOf(bp.number);
                  const gs = GROUP_STYLE[g];
                  return (
                  <td key={j} className="px-2 py-2 text-center">
                    <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-gray-900 text-[11px] ring-2 ring-offset-1 ${gs.ring} bg-white`} title={`${g} · Consensus ${consensus.scoreOf(bp.number) >= 0 ? '+' : ''}${consensus.scoreOf(bp.number)}`}>{bp.number}</span>
                    <span className={`text-[8px] ml-0.5 font-bold ${tagColors[bp.tag] || 'text-gray-400'}`}>{bp.tag}</span>
                  </td>
                  );
                })}
                {!t.ball_profiles && (t.balls || []).map((b, j) => {
                  const g = consensus.groupOf(b);
                  const gs = GROUP_STYLE[g];
                  return (
                  <td key={j} className="px-2 py-2 text-center">
                    <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-gray-900 text-[11px] ring-2 ring-offset-1 ${gs.ring} bg-white`} title={`${g} · Consensus ${consensus.scoreOf(b) >= 0 ? '+' : ''}${consensus.scoreOf(b)}`}>{b}</span>
                  </td>
                  );
                })}
                {(t.lucky_stars || []).map((ls, j) => <td key={j} className="px-2 py-2 text-center font-mono font-bold text-amber-600">{ls}</td>)}
                <td className="px-2 py-2 text-center text-[10px] font-bold">{t.hml_pattern || '-'}</td>
                <td className="px-2 py-2 text-center text-[10px] font-bold text-indigo-700">{t.composition || '-'}</td>
                <td className="px-2 py-2 text-center">
                  <div className="flex justify-center gap-0.5">
                    {["GREEN","YELLOW","RED"].map(gk => (
                      <span key={gk} className={`text-[9px] font-bold px-1 py-0.5 rounded ${GROUP_STYLE[gk].chip}`}>{gk[0]}{mix[gk]}</span>
                    ))}
                  </div>
                </td>
                <td className="px-2 py-2 text-center">
                  <QualityBadge tier={q.tier} score={q.score} size="xs" />
                </td>
                <td className="px-2 py-2 text-right font-mono font-bold text-indigo-700">{t.score}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  ));
}

export default function MyPredictionMaker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [db, setDb] = useState({ C1: null, C2: null, C3: null, C4: null, C5: null });
  const [activeTab, setActiveTab] = useState('C1');
  const [generating, setGenerating] = useState({ C1: false, C2: false, C3: false, C4: false, C5: false });
  const [results, setResults] = useState({ C1: null, C2: null, C3: null, C4: null, C5: null });
  const consensus = useConsensusGroups();

  const loadDB = async () => {
    try {
      const res = await axios.get(`${API}/analysis/constraint-sets`);
      setDb({ C1: res.data?.C1 || null, C2: res.data?.C2 || null, C3: res.data?.C3 || null, C4: res.data?.C4 || null, C5: res.data?.C5 || null });
    } catch (_e) { /* constraint-sets fetch failed — page still functions without them */ }
  };

  useEffect(() => { loadDB().finally(() => setLoading(false)); }, []);

  const clearSet = async (key) => {
    // Confirm then POST with the target set nulled, preserving other sets
    if (!window.confirm(`Clear ALL constraint selections for ${key}? This cannot be undone.`)) return;
    try {
      const payload = { C1: db.C1, C2: db.C2, C3: db.C3, C4: db.C4, C5: db.C5 };
      payload[key] = null;
      await axios.post(`${API}/analysis/constraint-sets`, payload);
      setDb(prev => ({ ...prev, [key]: null }));
      setResults(prev => ({ ...prev, [key]: null }));
      toast.success(`${key}: all constraints cleared`);
    } catch {
      toast.error(`Failed to clear ${key}`);
    }
  };

  const clearAllSets = async () => {
    if (!window.confirm("Clear ALL constraints across ALL 5 sets (C1-C5)? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/analysis/constraint-sets`, { C1: null, C2: null, C3: null, C4: null, C5: null });
      setDb({ C1: null, C2: null, C3: null, C4: null, C5: null });
      setResults({ C1: null, C2: null, C3: null, C4: null, C5: null });
      toast.success("All 5 constraint sets cleared");
    } catch {
      toast.error("Failed to clear all sets");
    }
  };

  const generateSet = async (key) => {
    setGenerating(prev => ({ ...prev, [key]: true }));
    try {
      const fresh = await axios.get(`${API}/analysis/constraint-sets`);
      const s = fresh.data?.[key];
      if (!s || Object.values(s).every(v => !v || v === '')) {
        toast.error(`No constraints for ${key}`);
        setGenerating(prev => ({ ...prev, [key]: false }));
        return;
      }
      const p = {};
      if (s.hml_pattern) p.hml_pattern = s.hml_pattern;
      if (s.sign_pattern) p.sign_pattern = s.sign_pattern;
      if (s.combo_pattern) p.combo_pattern = s.combo_pattern;
      if (s.sum_range_min != null) { p.sum_range_min = s.sum_range_min; p.sum_range_max = s.sum_range_max; }
      if (s.range_pattern) p.range_pattern = s.range_pattern;
      if (s.shared_prediction) p.shared_prediction = s.shared_prediction;
      if (s.temp_prefs && Object.keys(s.temp_prefs).length > 0) p.temp_prefs = s.temp_prefs;
      if (s.number_picks && Object.values(s.number_picks).some(v => v?.length > 0)) p.number_picks = s.number_picks;

      const res = await axios.post(`${API}/analysis/pattern-predictor/generate`, p);
      setResults(prev => ({ ...prev, [key]: res.data }));
      const c = res.data.combinations?.length || 0;
      if (c > 0) toast.success(`${key}: ${c} tickets`);
      else toast.error(`${key}: No tickets — too restrictive`);
    } catch { toast.error(`Failed ${key}`); }
    setGenerating(prev => ({ ...prev, [key]: false }));
  };

  const generateAll = async () => {
    const fresh = await axios.get(`${API}/analysis/constraint-sets`);
    setDb({ C1: fresh.data?.C1 || null, C2: fresh.data?.C2 || null, C3: fresh.data?.C3 || null, C4: fresh.data?.C4 || null, C5: fresh.data?.C5 || null });
    for (const key of ['C1', 'C2', 'C3', 'C4', 'C5']) {
      const s = fresh.data?.[key];
      if (s && Object.values(s).some(v => v && v !== '')) await generateSet(key);
    }
  };

  const total = ['C1', 'C2', 'C3', 'C4', 'C5'].reduce((s, k) => s + (results[k]?.combinations?.length || 0), 0);
  const tabs = ['C1', 'C2', 'C3', 'C4', 'C5'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-btn"><ArrowLeft size={20} /></button>
            <h1 className="text-xl font-bold text-gray-900">W44: My Prediction Maker with Constraints</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={async () => { await loadDB(); toast.success('Refreshed'); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50" data-testid="refresh-btn">
              <ArrowsClockwise size={14} /> Refresh
            </button>
            <button onClick={clearAllSets}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-rose-300 text-rose-600 rounded-lg text-xs font-bold hover:bg-rose-50" data-testid="clear-all-sets-btn"
              title="Remove all constraint selections across all 5 sets">
              <Eraser size={14} weight="bold" /> Clear All Sets
            </button>
            <button onClick={generateAll} disabled={Object.values(generating).some(Boolean)}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition disabled:opacity-50" data-testid="gen-all-btn">
              {Object.values(generating).some(Boolean) ? <CircleNotch size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
              Generate All
            </button>
            {total > 0 && <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">{total} tickets</span>}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : (
          <>
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-lg px-4 py-2.5 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="mpm-consensus-legend">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-bold text-slate-700">🚦 Each ball is ringed by its Consensus group (live gauge):</span>
                <GroupLegend groups={consensus.groups} />
              </div>
              <ConsensusRefLink />
            </div>

            <div className="flex gap-1 border-b border-gray-200 mb-6">
              {tabs.map(tab => {
                const applied = CONSTRAINT_DEFS.filter(d => getDisplay(db[tab], d)).length;
                const hasResults = results[tab]?.combinations?.length > 0;
                return (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`px-6 py-3 text-sm font-bold transition border-b-2 -mb-[1px] flex items-center gap-2 ${activeTab === tab ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    data-testid={`tab-${tab}`}>
                    Window {tab}
                    {applied > 0 && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{applied}</span>}
                    {hasResults && <span className="w-2 h-2 rounded-full bg-green-500" />}
                  </button>
                );
              })}
            </div>

            {tabs.map(tab => {
              if (tab !== activeTab) return null;
              return <WindowTab key={tab} setKey={tab} data={db[tab]} generating={generating[tab]} onGenerate={generateSet} onClear={clearSet} results={results[tab]} navigate={navigate} consensus={consensus} />;
            })}
          </>
        )}
      </main>
    </div>
  );
}
